/**
 * Confirmation dialog — shared modal for destructive actions.
 *
 * Mirrors the CreateWorkspaceDialog modal pattern: scrim + card +
 * role="dialog" aria-modal="true". No native <dialog> element (kept
 * consistent with the rest of the app).
 *
 * Keyboard: opening moves focus to the first enabled control (Cancel, the
 * safe choice), Tab and Shift+Tab stay inside the card, and closing gives
 * focus back to whatever held it before the dialog opened.
 *
 * `Esc` and the scrim close the dialog. `busy` disables both buttons, the
 * scrim and `Esc`, so a rapid second click can't double-fire the mutation and
 * an in-flight write can't be dismissed half-way.
 */

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  busy?: boolean
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea'

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
}: ConfirmDialogProps) {
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, busy])

  // Keyed on `open` only: callers pass inline handlers, and re-running this
  // on every render would keep pulling focus back to Cancel.
  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const card = cardRef.current
    const first = card?.querySelector<HTMLElement>('button:not([disabled])')
    if (first) first.focus()
    else card?.focus()
    return () => {
      if (opener?.isConnected) opener.focus()
    }
  }, [open])

  if (!open) return null

  function trapTab(e: ReactKeyboardEvent) {
    const card = cardRef.current
    if (e.key !== 'Tab' || !card) return
    const items = [...card.querySelectorAll<HTMLElement>(FOCUSABLE)]
    if (items.length === 0) {
      e.preventDefault()
      return
    }
    const first = items[0]!
    const last = items[items.length - 1]!
    const active = document.activeElement
    const inside = !!active && card.contains(active)
    if (e.shiftKey && (active === first || !inside)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onKeyDown={trapTab}
      onClick={() => {
        if (!busy) onClose()
      }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        className="card w-full max-w-sm p-5 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-base font-semibold">{title}</h2>
        {body && <div className="mb-4 text-xs text-surface-500">{body}</div>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost min-h-11" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={
              destructive
                ? 'rounded-md min-h-11 bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'
                : 'btn-primary'
            }
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
